<template>
  <div>
    <el-form
      ref="profileForm"
      :model="formData"
      :rules="rules"
      label-position="right"
      v-loading="saving">

      <el-row :gutter="20">
        <el-col :lg="8" :md="24">
          <el-form-item :label="$t('modules.profile.fields.firstName.label')" prop="first_name">
            <el-input type="text" v-model.lazy.trim="formData.first_name" :placeholder="$t('modules.profile.fields.firstName.placeholder')"></el-input>
          </el-form-item>
        </el-col>
        <el-col :lg="8" :md="24">
          <el-form-item :label="$t('modules.profile.fields.lastName.label')" prop="last_name">
            <el-input type="text" v-model.lazy.trim="formData.last_name" :placeholder="$t('modules.profile.fields.lastName.placeholder')"></el-input>
          </el-form-item>
        </el-col>
        <el-col :lg="8" :md="24">
          <el-form-item :label="$t('modules.profile.fields.ssn.label')" prop="ssn">
            <el-input type="number" v-model.lazy.trim="formData.ssn" :placeholder="$t('modules.profile.fields.ssn.placeholder')"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :lg="12" :md="24">
          <el-form-item :label="$t('modules.profile.fields.address.label')" prop="address">
            <el-input type="text" v-model.lazy="formData.address" :placeholder="$t('modules.profile.fields.address.placeholder')"></el-input>
          </el-form-item>
        </el-col>
        <el-col :lg="12" :md="24">
          <el-form-item :label="$t('modules.profile.fields.address2.label')" prop="address2">
            <el-input type="text" v-model.lazy="formData.address2" :placeholder="$t('modules.profile.fields.address2.placeholder')"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :lg="12" :md="24">
          <el-form-item :label="$t('modules.profile.fields.city.label')" prop="city">
            <el-input type="text" v-model.lazy="formData.city" :placeholder="$t('modules.profile.fields.city.placeholder')"></el-input>
          </el-form-item>
        </el-col>
        <el-col :lg="6" :md="24">
          <el-form-item :label="$t('modules.profile.fields.state.label')" prop="state">
            <el-select
              v-model="formData.state"
              filterable
              remote
              reserve-keyword
              :placeholder="$t('modules.profile.fields.state.placeholder')">
              <el-option
                v-for="(state, index) in defaults.states"
                :key="index"
                :label="state.name"
                :value="state.name">
              </el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :lg="6" :md="24">
          <el-form-item :label="$t('modules.profile.fields.zip.label')" prop="zip">
            <el-input type="text" v-model.lazy.trim="formData.zip" :placeholder="$t('modules.profile.fields.zip.placeholder')"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :lg="12" :md="24">
          <el-form-item :label="$t('modules.profile.fields.phone.label')" prop="phone">
            <el-input type="text" v-model.lazy="formData.phone" :placeholder="$t('modules.profile.fields.phone.placeholder')"></el-input>
          </el-form-item>
        </el-col>
        <el-col :lg="12" :md="24">
          <el-form-item :label="$t('modules.profile.fields.title.label')" prop="title">
            <el-input type="text" v-model.lazy="formData.title" :placeholder="$t('modules.profile.fields.title.placeholder')"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20" align="middle">
        <el-col>
          <el-form-item v-if="showButton">
            <el-button type="primary" @click="submitForm">{{ $t('buttons.save') }}</el-button>
          </el-form-item>
        </el-col>
      </el-row>

    </el-form>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import { showSuccessMessage, showErrorMessage } from '@/ui/message';
  import validations from '@/utils/validations';
  import formPopulator from '@/mixins/form_populator';

  import USStates from '@/mixins/states_us';

  export default {
    name: 'UserProfileForm',
    props: {
      userId: {
        type: Number,
      },
      profile: {
        type: Object,
      },
      showButton: {
        type: Boolean,
        default: true,
      },
      onSuccess: {
        type: Function,
      },
      onError: {
        type: Function,
      },
    },
    mixins: [formPopulator, USStates],
    created() {
      if (this.editMode) {
        this.populateFormWithModel(this.profile);
      }
      this.defaults.states = this.getStates();
    },
    data() {
      return {
        rules: {
          first_name: [
            { ...validations.required },
          ],
          last_name: [
            { ...validations.required },
          ],
        },
        formData: {
          first_name: '',
          last_name: '',
          ssn: '',
          address: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          title: '',
        },
        defaults: {
          countries: [],
          states: [],
        },
      };
    },
    computed: {
      ...mapGetters('entities/profiles', ['creating', 'isUpdating', 'formModalOpened']),

      editMode() {
        return this.profile;
      },

      saving() {
        return this.editMode ? this.isUpdating(this.profile.id) : this.creating;
      },
    },

    methods: {
      ...mapActions('entities/profiles', ['createProfile', 'updateProfile']),

      submitForm() {
        this.$refs.profileForm.validate((valid) => {
          if (valid) {
            if (this.profile) {
              this.updateProfile({ id: this.profile.id, data: this.formData })
                .then((response) => {
                  if (response.status) {
                    if (this.onSuccess) this.onSuccess(response);

                    showSuccessMessage(this.$t('modules.profile.messages.updated'));
                  } else {
                    if (this.onError) this.onError(response);

                    showErrorMessage(response.error);
                  }
                });
            } else {
              this.createProfile({ user_id: this.userId, ...this.formData })
                .then((response) => {
                  if (response.status) {
                    if (this.onSuccess) this.onSuccess(response);

                    showSuccessMessage(this.$t('modules.profile.messages.created'));
                  } else {
                    if (this.onError) this.onError(response);

                    showErrorMessage(response.error);
                  }
                });
            }
          } else {
            showErrorMessage(this.$t('form.messages.notValid'));

            return false;
          }

          return true;
        });
      },
    },
    watch: {
      profile(newValue) {
        if (newValue) {
          this.populateFormWithModel(newValue);
        } else {
          this.$refs.profileForm.resetFields();
        }
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/profile/UserProfileForm.vue